import './index.css';
import { Band, ZoningBand } from '../../../../screens/ProfitCalculations/types';
import { DomainTuple } from 'victory';
import { useMemo } from 'react';
import { isZoningBand } from '..';
import { colors } from '../../../../colors';

type DataPoint = {
  low: number;
  open: number;
  close: number;
  high: number;
  x: number;
  x2: number;
};

interface Props {
  channel:
    | { data: Band | ZoningBand | undefined; signalPeriod: number | null }
    | undefined;
  label: string;
  domain:
    | {
        x: DomainTuple;
        y: DomainTuple;
      }
    | undefined;
  candles: DataPoint[];
  skip: boolean;
}

type LegendItem = {
  color: string;
  label: string;
};

const ChannelChartLegend = (props: Props) => {
  const { channel, label, domain, candles, skip } = props;

  const legend = useMemo(() => {
    if (!channel?.data) {
      return undefined;
    }

    const legendItems: LegendItem[] = [
      {
        color: colors.ligthBlue,
        label,
      },
    ];

    if (isZoningBand(channel.data)) {
      legendItems.unshift({
        color: colors.red,
        label: 'SELL Zone',
      });
      legendItems.push({
        color: colors.green,
        label: 'BUY Zone',
      });
    }

    legendItems.push({
      color: colors.orange,
      label: `Signal EMA (${channel.signalPeriod})`,
    });

    return legendItems;
  }, [channel, label]);

  const position = useMemo(() => {
    if (!domain) {
      return {
        top: '60px',
        left: '60px',
      };
    }

    let found = false;

    /** We want to position the legend on a poart of the chart that has no candles on it */
    const yRatio = 1 / 5;
    const xRatio = 1 / 13;

    const domainYRange = (domain.y[1] as number) - (domain.y[0] as number);
    const domainXRange = (domain.x[1] as number) - (domain.x[0] as number);

    /** TOP LEFT */
    const topLeftArea = {
      y: [
        (domain.y[1] as number) - domainYRange * yRatio,
        domain.y[1] as number,
      ],
      x: [
        domain.x[0] as number,
        (domain.x[0] as number) + domainXRange * xRatio,
      ],
    };

    const leftCandles = candles.filter((c) => c.x < topLeftArea.x[1]);

    const maxValueTopLeft = Math.max(
      ...leftCandles.map((c) => Math.max(c.close, c.open)),
    );

    if (maxValueTopLeft < topLeftArea.y[0]) {
      if (!found && skip) {
        found = true;
      } else {
        /** We use default values */
        return {
          top: '60px',
          left: '60px',
        };
      }
    }

    /** TOP RIGHT */
    const topRightArea = {
      y: [
        (domain.y[1] as number) - domainYRange * yRatio,
        domain.y[1] as number,
      ],
      x: [
        (domain.x[1] as number) - domainXRange * xRatio,
        domain.x[1] as number,
      ],
    };

    const rightCandles = candles.filter((c) => c.x >= topRightArea.x[0]);

    const maxValueTopRight = Math.max(
      ...rightCandles.map((c) => Math.max(c.close, c.open)),
    );

    if (maxValueTopRight < topRightArea.y[0]) {
      if (!found && skip) {
        found = true;
      } else {
        return {
          top: '60px',
          right: '100px',
        };
      }
    }

    /** BOTTOM LEFT */
    const bottomLeftArea = {
      y: [
        domain.y[0] as number,
        (domain.y[0] as number) + domainYRange * yRatio,
      ],
      x: [
        domain.x[0] as number,
        (domain.x[0] as number) + domainXRange * xRatio,
      ],
    };

    const minValueBottomLeft = Math.min(
      ...leftCandles.map((c) => Math.min(c.close, c.open)),
    );

    if (minValueBottomLeft > bottomLeftArea.y[1]) {
      if (!found && skip) {
        found = true;
      } else {
        return {
          bottom: '10px',
          left: '60px',
        };
      }
    }

    /** BOTTOM RIGHT */
    const bottomRightArea = {
      y: [
        domain.y[0] as number,
        (domain.y[0] as number) + domainYRange * yRatio,
      ],
      x: [
        (domain.x[1] as number) - domainXRange * xRatio,
        domain.x[1] as number,
      ],
    };

    const minValueBottomRight = Math.min(
      ...rightCandles.map((c) => Math.min(c.close, c.open)),
    );

    if (minValueBottomRight > bottomRightArea.y[1]) {
      if (!found && skip) {
        found = true;
      } else {
        return {
          bottom: '10px',
          right: '100px',
        };
      }
    }

    /** Default */
    return {
      top: '60px',
      left: '60px',
    };
  }, [candles, domain, skip]);

  if (!legend) {
    return null;
  }

  return (
    <div className="candlesticks-chart-legend-root" style={{ ...position }}>
      {legend.map(({ label, color }) => (
        <div className="candlesticks-chart-legend-row" key={label}>
          <div
            className="candlesticks-chart-legend-color"
            style={{ backgroundColor: color }}
          ></div>
          <span className="candlesticks-chart-legend-text">{label}</span>
        </div>
      ))}
    </div>
  );
};

export default ChannelChartLegend;
